import { base64UrlToBytes, parseSignedPass, type PassClaims } from './passPayload';

/**
 * Офлайн-проверка подписи пропуска (ТЗ FR-PASS-01, риск 5 ТЗ §12; ADR-002).
 *
 * Ключ проверки выдаёт сервер: это **производный от мастер-ключа ключ своего
 * объекта** (HKDF-SHA256 на стороне API). Мастер-ключ наружу не выдаётся —
 * компрометация устройства модератора ограничена одним предприятием.
 *
 * Подпись — HMAC-SHA256 от строки `PT1.<kid>.<code>.<payload>`; сравнение
 * выполняется через crypto.subtle.verify (константное по времени).
 */

export type OfflineFailureReason =
  | 'malformed'
  | 'invalid_signature'
  | 'expired'
  | 'wrong_enterprise'
  | 'revoked';

export interface OfflineVerification {
  ok: boolean;
  reason?: OfflineFailureReason;
  claims?: PassClaims;
  checkedAt: string;
}

/**
 * Импорт ключа проверки. Ключ приходит с сервера в base64 и уже является
 * производным для конкретного предприятия.
 */
export async function importVerificationKey(keyBase64: string): Promise<CryptoKey> {
  const raw = base64UrlToBytes(keyBase64);
  return crypto.subtle.importKey(
    'raw',
    raw as unknown as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['verify'],
  );
}

/**
 * Проверка пропуска без обращения к сети.
 * Возвращает причину отказа; при успехе — claims пропуска.
 */
export async function verifyPassOffline(
  raw: string,
  verificationKeyBase64: string,
  enterpriseId: string,
  now: Date = new Date(),
): Promise<OfflineVerification> {
  const checkedAt = now.toISOString();
  const parsed = parseSignedPass(raw);

  if (!parsed) {
    return { ok: false, reason: 'malformed', checkedAt };
  }

  let signatureValid = false;
  try {
    const key = await importVerificationKey(verificationKeyBase64);
    signatureValid = await crypto.subtle.verify(
      'HMAC',
      key,
      base64UrlToBytes(parsed.signature) as unknown as BufferSource,
      new TextEncoder().encode(parsed.signedPart) as unknown as BufferSource,
    );
  } catch {
    signatureValid = false;
  }

  if (!signatureValid) {
    return { ok: false, reason: 'invalid_signature', claims: parsed.claims, checkedAt };
  }

  if (parsed.claims.enterpriseId !== enterpriseId) {
    return { ok: false, reason: 'wrong_enterprise', claims: parsed.claims, checkedAt };
  }

  const expiresAt = Date.parse(parsed.claims.expiresAt);
  if (!Number.isNaN(expiresAt) && expiresAt < now.getTime()) {
    return { ok: false, reason: 'expired', claims: parsed.claims, checkedAt };
  }

  return { ok: true, claims: parsed.claims, checkedAt };
}
